import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useState } from "react";
import { getMyHoursProject } from "../Controllers/projectController";

const setMyHoursProject = async (
  project,
  newCode,
  setAlert,
  setMhProject,
  setMhError,
  setSyncToMyHours,
  setLoading,
) => {
  setLoading(true);
  const projects = await getMyHoursProject(
    project.code ?? project.uid,
    newCode,
    setAlert,
  );
  if (projects === false) {
    setMhProject(projects);
  } else if (projects.length === 1) {
    setMhProject(projects[0]);
  } else if (projects.length >= 2) {
    setMhError(
      `Automatic My Hours sync is disabled! Found multiple matching projects from My Hours for codes
        ${project.code ?? project.uid} and ${newCode}, expected only one.
        Make sure ${newCode} is the correct code and that
        there is only one matching project in My Hours for these codes!`,
    );
    setSyncToMyHours(false);
  }
  setLoading(false);
};

const MyHoursCompare = ({
  newCode,
  project,
  syncToMyHours,
  setSyncToMyHours,
  setAlert,
}) => {
  const [mhError, setMhError] = useState("");
  const [mhProject, setMhProject] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setMyHoursProject(
      project,
      newCode,
      setAlert,
      setMhProject,
      setMhError,
      setSyncToMyHours,
      setLoading,
    );
  }, []);

  useEffect(() => {
    if (
      mhProject &&
      project &&
      mhProject.name ===
        (project.acronym
          ? `${project.acronym}: ${project.name}`
          : project.name) &&
      mhProject.code === newCode &&
      mhProject.client_name === project.client_name
    ) {
      setSyncToMyHours(false);
    }
  }, [mhProject, project, newCode, setSyncToMyHours]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Stack direction="column">
      {mhProject === false && ((project.code ?? project.uid) || newCode) && (
        <Typography color="red" fontWeight="bold">
          {`An unexpected error occured while trying to get project from My
            Hours. If a project with code ${newCode} or 
            ${project.code ? project.code : project.uid} already exists in My
            Hours then ensure it is the same project to avoid overwriting details of a wrong
            project.`}
        </Typography>
      )}
      {mhProject === null && mhError !== "" && (
        <Typography color="red" fontWeight="bold">
          {mhError}
        </Typography>
      )}
      {mhProject !== false && mhProject != null && (
        <Stack direction="column" paddingTop={2} spacing={1}>
          {mhProject.name ===
            (project.acronym
              ? `${project.acronym}: ${project.name}`
              : project.name) &&
          mhProject.code === newCode &&
          mhProject.client_name === project.client_name ? (
            <Typography fontSize={20}>
              There are no changes to My Hours
            </Typography>
          ) : (
            <Typography fontSize={20}>Changes to My Hours</Typography>
          )}
          {mhProject.name !==
            (project.acronym
              ? `${project.acronym}: ${project.name}`
              : project.name) && (
            <Stack direction="row" alignItems={"center"} spacing={1}>
              <Typography>Name:</Typography>
              <Typography>
                {mhProject.name}
                <strong>
                  {" => "}
                  {project.acronym
                    ? `${project.acronym}: ${project.name}`
                    : project.name}
                </strong>
              </Typography>
            </Stack>
          )}
          {mhProject.code !== (newCode || project.code || project.uid) && (
            <Stack direction="row" alignItems={"center"} spacing={1}>
              <Typography>Code:</Typography>
              <Typography>
                {mhProject.code}
                <strong>
                  {" => "}
                  {newCode || project.code || project.uid}
                </strong>
              </Typography>
            </Stack>
          )}
          {mhProject.client_name !== project.client_name && (
            <Stack direction="row" alignItems={"center"} spacing={1}>
              <Typography>Client name:</Typography>
              <Typography>
                {mhProject.client_name}
                <strong>
                  {" => "}
                  {project.client_name}
                </strong>
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
      {((mhProject !== false && mhProject == null) ||
        !((project.code ?? project.uid) || newCode)) && (
        <Stack direction="column">
          <Typography paddingBottom={1} fontSize={20}>
            Create new project in My Hours
          </Typography>
          <Stack direction="row" alignItems={"center"} spacing={1}>
            <Typography>Name:</Typography>
            <Typography>
              {project.acronym
                ? `${project.acronym}: ${project.name}`
                : project.name}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems={"center"} spacing={1}>
            <Typography>Code:</Typography>
            <Typography>
              {newCode || (project.code ?? project.uid) || "Autogenerated"}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems={"center"} spacing={1}>
            <Typography>Client name:</Typography>
            <Typography>{project.client_name || "-"}</Typography>
          </Stack>
        </Stack>
      )}
      {!(
        mhProject?.name ===
          (project.acronym
            ? `${project.acronym}: ${project.name}`
            : project.name) &&
        mhProject?.code === newCode &&
        mhProject?.client_name === project.client_name
      ) &&
        setSyncToMyHours && (
          <Box
            sx={{
              position: "relative",
              border: "1px solid rgba(0, 0, 0, 0.23)", // matches TextField default
              borderRadius: 1,
              padding: 1,
              mt: 2,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  disabled={mhError !== ""}
                  checked={syncToMyHours}
                  onChange={(event, checked) => {
                    setSyncToMyHours(checked);
                  }}
                />
              }
              label="Sync to My Hours"
              labelPlacement="start"
              sx={{
                // make the label span only hug its text
                "& .MuiFormControlLabel-label": {
                  cursor: "pointer",
                  width: "auto",
                  fontWeight: "bold",
                },
                width: "fit-content", // collapse whole thing
              }}
            />
          </Box>
        )}
    </Stack>
  );
};

export default MyHoursCompare;
